import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Building2, Map } from "lucide-react";
import { get } from "@/lib/api";

const ICON = { state: Map, city: MapPin, community: Building2 };

export const SearchBar = ({ size = "lg", initial = "", testid = "hero-search" }) => {
  const [q, setQ] = useState(initial);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const nav = useNavigate();
  const timer = useRef();

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setItems([]); return; }
    timer.current = setTimeout(() => get("/suggest", { q }).then(setItems).catch(() => setItems([])), 220);
  }, [q]);

  const go = (url) => { setOpen(false); nav(url); };
  const submit = (e) => {
    e.preventDefault();
    if (active >= 0 && items[active]) return go(items[active].url);
    go(`/search${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`);
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(items.length - 1, a + 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(-1, a - 1)); }
    if (e.key === "Escape") setOpen(false);
  };
  const big = size === "lg";

  return (
    <form onSubmit={submit} role="search" className="relative w-full" data-testid={`${testid}-form`}>
      <div className={`flex items-center bg-white rounded-full border border-[#E2E8F0] shadow-lg ${big ? "p-2" : "p-1"}`}>
        <Search className={`text-[#03A896] shrink-0 ${big ? "h-6 w-6 ml-4" : "h-5 w-5 ml-3"}`} aria-hidden />
        <label htmlFor={`${testid}-input`} className="sr-only">Search by city, state, ZIP or HOA name</label>
        <input id={`${testid}-input`} value={q} autoComplete="off" data-testid={`${testid}-input`}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(-1); }} onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)} onKeyDown={onKey}
          role="combobox" aria-expanded={open && items.length > 0} aria-controls={`${testid}-listbox`} aria-autocomplete="list"
          placeholder="City, state, ZIP or HOA name"
          className={`flex-1 min-w-0 bg-transparent outline-none px-3 text-[#0F172A] placeholder:text-slate-500 ${big ? "text-base sm:text-lg py-3" : "text-sm py-2"}`} />
        <button type="submit" data-testid={`${testid}-button`}
          className={`rounded-full bg-[#03A896] text-white font-semibold hover:bg-[#0B2150] transition-colors ${big ? "px-5 sm:px-8 py-3 text-base" : "px-4 py-2 text-sm"}`}>
          {big ? <span><span className="hidden sm:inline">Search Neighborhoods</span><span className="sm:hidden">Search</span></span> : "Search"}
        </button>
      </div>
      {open && items.length > 0 && (
        <ul id={`${testid}-listbox`} role="listbox" data-testid={`${testid}-suggestions`}
          className="absolute z-30 mt-2 w-full rounded-2xl bg-white border border-[#E2E8F0] shadow-2xl py-2 text-left max-h-96 overflow-auto">
          {items.map((it, i) => {
            const Icon = ICON[it.type];
            return (
              <li key={it.url} role="option" aria-selected={i === active}>
                <button type="button" onMouseDown={() => go(it.url)} data-testid={`suggestion-${i}`}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#E6F4F1] ${i === active ? "bg-[#E6F4F1]" : ""}`}>
                  <Icon className="h-4 w-4 text-[#027A6D] shrink-0" aria-hidden />
                  <span className="text-sm text-[#0F172A] font-medium">{it.label}</span>
                  {it.sub && <span className="text-xs text-slate-500">{it.sub}</span>}
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-500">{it.type}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </form>
  );
};
